import type { ColumnDef, Getter } from "@tanstack/react-table";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SortingRule } from "react-table";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import useSWR from "swr";
import { PrimaryButtonMediumV2 } from "../../../../../../components/Buttons/Buttons";
import { DropDown } from "../../../../../../components/DropDown/DropDown";
import { IDMobile } from "../../../../../../components/IDs/IDs";
import { DropdownContainer } from "../../../../../../components/Layout/Layout";
import { Pagination } from "../../../../../../components/Pagination/Pagination";
import { createChangePerPageHandler } from "../../../../../../components/Pagination/util";
import { Table } from "../../../../../../components/Table/Table";
import { TooltipPortal } from "../../../../../../components/TooltipModal/TooltipPortal";
import { H3 } from "../../../../../../components/Typography/Typography";
import { endpoints } from "../../../../../../endpoints";
import { ContentWrapper } from "../../../../../../layout/portalPageLayout";
import type {
  SupportedLanguage,
  WithPagination,
} from "../../../../../../types/types";
import type { UploadedAssetSchema } from "../../../../../../types/types.PIM";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  formatDateTime,
  toTitleCase,
  useStoreState,
} from "../../../../../../util/util";
import { spacingTokens } from "../../../../../../designTokens";

const AssetsListContainer = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_3xl")};
  display: flex;
  flex-direction: column;
`;

const OverflowCell = styled.span`
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
`;

export const UploadedAssetsList = ({
  setStage,
  onSelect,
  removeExternalAssetUploads,
  isCheckBoxRequired = false,
}: {
  setStage: Dispatch<SetStateAction<"list" | "add_files">>;
  onSelect?: (selectedAssets: string[]) => void;
  isCheckBoxRequired?: boolean;
  removeExternalAssetUploads?: boolean;
}) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();

  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "desc",
  });
  const [tableData, setTableData] = useState<UploadedAssetSchema[]>([]);
  const [offset, setOffset] = useState(0);
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [should_mutate, set_should_mutate] = useState(true);

  const buildTooltipHtml = (values: string[]) =>
    `<ul style="margin:0;padding:0 0 0 16px">${values
      .map((v) => `<li>${v}</li>`)
      .join("")}</ul>`;

  const tableColumns = useMemo<ColumnDef<UploadedAssetSchema>[]>(
    () => [
      {
        header: t("Upload ID"),
        accessorKey: "number",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <IDMobile>{getValue()}</IDMobile>
        ),
      },
      {
        header: t("Asset Type"),
        accessorKey: "asset_type",
        enableSorting: false,
        cell: ({
          row,
          getValue,
        }: {
          row: { original: UploadedAssetSchema };
          getValue: Getter<UploadedAssetSchema["asset_type"]>;
        }) => {
          const uniqueTypes = [
            ...new Set(
              row.original.assets.map((a) => a.asset_type).filter(Boolean)
            ),
          ].map(toTitleCase);
          if (uniqueTypes.length === 0) {
            return <span>{getValue() ? toTitleCase(getValue()) : "--"}</span>;
          }
          const displayText = uniqueTypes.join(", ");
          const tooltipHtml =
            uniqueTypes.length > 1 ? buildTooltipHtml(uniqueTypes) : undefined;
          return (
            <>
              <OverflowCell
                data-tip={tooltipHtml}
                data-for="uploaded-assets-type-tooltip"
                data-html={tooltipHtml ? true : undefined}
              >
                {displayText}
              </OverflowCell>
              <TooltipPortal>
                <ReactTooltip
                  id="uploaded-assets-type-tooltip"
                  place="top"
                  effect="solid"
                  multiline={true}
                />
              </TooltipPortal>
            </>
          );
        },
      },
      {
        header: t("Category"),
        accessorKey: "category",
        enableSorting: false,
        cell: ({
          row,
          getValue,
        }: {
          row: { original: UploadedAssetSchema };
          getValue: Getter<UploadedAssetSchema["category"]>;
        }) => {
          const uniqueCategories = [
            ...new Set(
              row.original.assets.map((a) => a.category).filter(Boolean)
            ),
          ].map(toTitleCase);
          if (uniqueCategories.length === 0) {
            return (
              <span>
                {getValue()?.name ? toTitleCase(getValue()?.name) : "--"}
              </span>
            );
          }
          const displayText = uniqueCategories.join(", ");
          const tooltipHtml =
            uniqueCategories.length > 1
              ? buildTooltipHtml(uniqueCategories)
              : undefined;
          return (
            <>
              <OverflowCell
                data-tip={tooltipHtml}
                data-for="uploaded-assets-category-tooltip"
                data-html={tooltipHtml ? true : undefined}
              >
                {displayText}
              </OverflowCell>
              <TooltipPortal>
                <ReactTooltip
                  id="uploaded-assets-category-tooltip"
                  place="top"
                  effect="solid"
                  multiline={true}
                />
              </TooltipPortal>
            </>
          );
        },
      },
      {
        header: t("Language"),
        accessorKey: "language",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<SupportedLanguage> }) => (
          <span>{getValue()?.toLocaleUpperCase()}</span>
        ),
      },
      {
        header: t("# of assets"),
        enableSorting: false,
        accessorKey: "number_of_assets",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: "Uploaded By",
        enableSorting: false,
        accessorKey: "modified_by",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: "Uploaded On",
        accessorKey: "modified_at",
        cell: (cell) => cell.renderValue(),
      },
    ],
    [t]
  );

  const {
    data: previousUploadsResponse,
    mutate,
    error,
  } = useSWR<WithPagination<{ data: UploadedAssetSchema[] }>>(
    [
      endpoints.v2_tenants_tenant_id_pim_assets_uploads(tenant_id),
      useMemo(
        () => ({
          params: {
            offset,
            limit: perPage,
            ...(sortingRules.sortBy ? { sort_by: sortingRules.sortBy } : {}),
            order_by: sortingRules.orderBy ?? "desc",
            ...(removeExternalAssetUploads !== undefined
              ? { external_assets: !removeExternalAssetUploads }
              : {}),
          },
        }),
        [
          offset,
          perPage,
          removeExternalAssetUploads,
          sortingRules.orderBy,
          sortingRules.sortBy,
        ]
      ),
    ],
    { revalidateOnMount: true }
  );

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const isLoading = !previousUploadsResponse && !error;

  useEffect(() => {
    const handlePreviousUpdatesData = ({
      data,
      pagination,
    }: WithPagination<{ data: UploadedAssetSchema[] }>) => {
      setTableData(
        data.map((data) => ({
          ...data,
          modified_at: formatDateTime(data.modified_at),
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };
    if (previousUploadsResponse) {
      handlePreviousUpdatesData(previousUploadsResponse);
    }
  }, [perPage, previousUploadsResponse]);

  useEffect(() => {
    if (should_mutate && !!mutate) {
      mutate();
      set_should_mutate(false);
    }
  }, [mutate, should_mutate]);

  const handleSelect = useCallback(
    (selectedRows: string[] | "all") => {
      if (onSelect && selectedRows !== "all") {
        onSelect(selectedRows);
      }
    },
    [onSelect]
  );

  return (
    <AssetsListContainer>
      {!isCheckBoxRequired && (
        <PrimaryButtonMediumV2
          style={{ maxWidth: "168px" }}
          onClick={() => setStage("add_files")}
        >
          {t("Start a new upload")}
        </PrimaryButtonMediumV2>
      )}
      <H3 style={{ margin: `${spacingTokens.spacing_3xl} 0 0` }}>
        {t("Previous Uploads")}
      </H3>
      <div>
        <DropdownContainer>
          <DropDown
            items={perPageItems}
            activeItem={perPage}
            textLeft={t("items") + ":"}
            textRight={t(" Per Page")}
            direction={"right"}
            className={"per_Page"}
            clickHandler={changePerPage}
          />
        </DropdownContainer>
        <ContentWrapper>
          <Table
            columns={tableColumns}
            data={tableData}
            isLoading={isLoading}
            error={error}
            handleSort={handleSort}
            lastChildleftAlign={true}
            handleSelectRows={isCheckBoxRequired ? handleSelect : undefined}
          />
          <Pagination
            pagination={tablePagination}
            offset={offset}
            handlePageClick={changePage}
          />
        </ContentWrapper>
      </div>
    </AssetsListContainer>
  );
};
